import { DefaultButton, IconButton, TooltipHost } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HealAPI } from '../../healApi';
import { ConfirmationStateType, Visit } from '../../redux/visits/types';
import { isFutureVisitStatus } from '../../types';
import { css } from '../../util/Css';
import styles from './PatientConfirmation.module.scss';

interface Props {
  visit: Visit;
  onUpdate: () => void;
}

const MANUAL_CONFIRMATION_DAYS = 7;

const PatientConfirmation: React.FC<Props> = ({ visit, onUpdate }: Props) => {
  const sectionTooltipId = useId('sectionTooltip');
  const statusButtonTooltipId = useId('statusButtonTooltip');
  const callButtonTooltipId = useId('callButtonTooltip');

  const [callButtonDisabled, setCallButtonDisabled] = useState(false);

  // Reset 'Send confirmation call' button if visit is changed w/o closing the VisitSidePanel
  useEffect(() => {
    visit && setCallButtonDisabled(false);
  }, [visit]);

  const isVisitUpcoming = useMemo(() => {
    const isPastDate = moment(visit.visitDateFormatted).tz(visit.timezone).isBefore(new Date(), 'day');
    const isUpcomingVisitStatus = isFutureVisitStatus(visit.status);
    return !isPastDate && isUpcomingVisitStatus;
  }, [visit]);

  const isVisitToday = useMemo(() => moment(visit.visitDateFormatted).tz(visit.timezone).isSame(new Date(), 'day'), [visit]);

  const isVisitInConfirmationWindow = useMemo(
    () =>
      isVisitUpcoming &&
      moment(visit.visitDateFormatted)
        .tz(visit.timezone)
        .isBefore(moment().add(MANUAL_CONFIRMATION_DAYS + 1, 'days'), 'day'),
    [visit, isVisitUpcoming]
  );

  const isVisitConfirmed = useMemo(() => [ConfirmationStateType.CONFIRMED, ConfirmationStateType.CONFIRMED_MANUAL].includes(visit.confirmationState), [visit.confirmationState]);

  const stateLabel = useMemo(
    () => (visit.confirmationState === ConfirmationStateType.CONFIRMED_MANUAL ? 'OPS CONFIRMED' : visit.confirmationState.replaceAll('_', ' ')),
    [visit.confirmationState]
  );

  const stateDescription = useMemo(() => {
    switch (visit.confirmationState) {
      case ConfirmationStateType.UNINITIATED:
        return isVisitUpcoming ? 'Patient has yet to be called to confirm this visit.' : 'Confirmation call was not attempted for this visit.';
      case ConfirmationStateType.AWAITING_RESPONSE:
        return isVisitUpcoming ? 'Patient has been called to confirm the visit but has not responded.' : 'Patient did not respond to attempts to confirm the visit.';
      case ConfirmationStateType.CONFIRMED:
        return 'Patient has confirmed this visit.';
      case ConfirmationStateType.CONFIRMED_MANUAL:
        return 'Ops has manually confirmed this visit.';
      case ConfirmationStateType.FAILED:
        return 'Patient did not confirm this visit.';
      case ConfirmationStateType.STOPPED:
      case ConfirmationStateType.CANCELLED:
        return 'Confirmation process has been stopped.';
      case ConfirmationStateType.EXEMPT:
        return 'Visit does not participate in the call confirmation process.';
    }
  }, [visit.confirmationState, isVisitUpcoming]);

  const setConfirmationState = useCallback(
    async (state: ConfirmationStateType) => {
      await HealAPI.setConfirmationState(visit.visitCode, state);
      onUpdate();
    },
    [onUpdate, visit.visitCode]
  );

  return (
    <div className={css(styles.visitFieldControl, styles.patientConfirmation)}>
      <div className={css(styles.visitFieldLabel, 'flex', 'flex-center')}>
        <span>Patient Confirmation</span>
        <TooltipHost
          id={sectionTooltipId}
          content="Patient is automatically sent a confirmation call a few days prior to their visit in participating markets. If they do not confirm, confirmation will be retried once a day until the scheduled visit day."
        >
          <IconButton iconProps={{ iconName: 'Info' }} title="Confirmation call info" ariaLabel="Confirmation call info" />
        </TooltipHost>
      </div>
      <div className={styles.visitFieldValue}>
        <div className={css(styles.confirmationState, styles[visit.confirmationState])}>{stateLabel}</div>
        <div className={styles.description}>{stateDescription}</div>

        <div className={styles.buttons}>
          <TooltipHost id={statusButtonTooltipId} content={`Manually changes the confirmation status. Must be within ${MANUAL_CONFIRMATION_DAYS} days of upcoming visit date.`}>
            {isVisitConfirmed ? (
              <DefaultButton
                text="Set unconfirmed"
                className={styles.unconfirmButton}
                onClick={() => setConfirmationState(ConfirmationStateType.AWAITING_RESPONSE)}
                disabled={!isVisitInConfirmationWindow}
              />
            ) : (
              <DefaultButton
                text="Set confirmed"
                className={styles.confirmButton}
                onClick={() => setConfirmationState(ConfirmationStateType.CONFIRMED_MANUAL)}
                disabled={!isVisitInConfirmationWindow}
              />
            )}
          </TooltipHost>

          <TooltipHost id={callButtonTooltipId} content="Manually sends a confirmation call to the patient in the background. Must be at least 1 day in advance of visit date.">
            <DefaultButton
              text="Send confirmation call"
              onClick={async () => {
                HealAPI.insertLog(visit.visitCode, 'Confirmation call sent manually via operations.');
                const response = await HealAPI.sendConfirmationCall(visit.visitCode);
                response.ok && response.data?.data && setCallButtonDisabled(true);
              }}
              className={styles.callButton}
              disabled={callButtonDisabled || isVisitConfirmed || isVisitToday || !isVisitInConfirmationWindow}
            />
          </TooltipHost>
        </div>
      </div>
    </div>
  );
};

export default PatientConfirmation;
